import type { MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue, watch } from 'vue';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
// noinspection ES6UnusedImports
import type {
  AttachTouch,
  ConfigurationOptions,
  HandledEvents,
  HandlerRef,
  HandlerSetter,
  HandlerSetterWithCancel,
  Setter,
  SwipeableCallbacks,
  SwipeableDirectionCallbacks,
  SwipeableProps,
  SwipeablePropsWithDefaultOptions,
  SwipeableState,
  SwipeDirections,
  SwipeEventData,
  Vector2,
} from './types';
import {
  LEFT,
  RIGHT,
  UP,
  DOWN,
  EVENT_SWIPED_DOWN,
  EVENT_SWIPED_LEFT,
  EVENT_SWIPED_UP,
  EVENT_SWIPED_RIGHT,
  EVENT_SWIPED_START,
  EVENT_SWIPED,
  EVENT_SWIPING,
  EVENT_TAP,
  EVENT_TOUCHSTART_OR_MOUSEDOWN,
  EVENT_TOUCHEND_OR_MOUSEUP,
} from './constants';

const defaultProps: ConfigurationOptions = {
  delta: 10,
  preventScrollOnSwipe: false,
  rotationAngle: 0,
  trackMouse: false,
  trackTouch: true,
  swipeDuration: Infinity,
  touchEventOptions: { passive: true },
};
const initialState: SwipeableState = {
  first: true,
  initial: [0, 0],
  start: 0,
  swiping: false,
  xy: [0, 0],
};
const mouseMove = 'mousemove';
const mouseUp = 'mouseup';
const touchEnd = 'touchend';
const touchMove = 'touchmove';
const touchStart = 'touchstart';

function getDirection(
  absX: number,
  absY: number,
  deltaX: number,
  deltaY: number,
): SwipeDirections {
  if (absX > absY) {
    if (deltaX > 0) {
      return RIGHT;
    }
    return LEFT;
  } if (deltaY > 0) {
    return DOWN;
  }
  return UP;
}

function rotateXYByAngle(pos: Vector2, angle: number): Vector2 {
  if (angle === 0) return pos;
  const angleInRadians = (Math.PI / 180) * angle;
  const x =
    pos[0] * Math.cos(angleInRadians) + pos[1] * Math.sin(angleInRadians);
  const y =
    pos[1] * Math.cos(angleInRadians) - pos[0] * Math.sin(angleInRadians);
  return [x, y];
}

/**
 * Event listeners:
 *  - Registered by `onStart()` / Unregistered by `cleanUpMouse` (called by `onUp`)
 *    - `document`: `mouseMove` -> `onMove`
 *    - `document`: `mouseUp` -> `onUp`
 *  - Registered by `attachTouch(el)` / Unregistered by `cleanup` (returned by `attachTouch(el)`):
 *    - `touchStart`
 *    - `touchMove`
 *    - `touchEnd`
 *
 * Callbacks:
 *  - `onStart()`:
 *    - `props.onTouchStartOrOnMouseDown`
 *  - `onMove()`:
 *    - `props.onSwipeStart`
 *    - `props.onSwiping`
 *  - `onEnd()`:
 *    - `props.onSwiped`
 *    - `props.onTap`
 *    - `props.onTouchEndOrOnMouseUp`
 */
function getHandlers(
  set: Setter,
  handlerProps: { trackMouse: boolean | undefined },
): [HandlerRef, AttachTouch] {
  /**
   * Registers:
   *  - `document`: `mouseMove` -> `onMove`
   *  - `document`: `mouseUp` -> `onUp`
   * Calls:
   *  - `props.onTouchStartOrOnMouseDown`
   */
  console.log('[vue3-swipable] getHandlers @all');
  const onStart = (event: HandledEvents) => {
    console.log('[vue3-swipable] getHandlers @ onStart');
    const isTouch = 'touches' in event;
    // if more than a single touch don't track, for now...
    if (isTouch && event.touches.length > 1) return;

    set((state, props) => {
      // setup mouse listeners on document to track swipe since swipe can leave container
      console.log('[vue3-swipable] getHandlers @ onStart @ SET');
      if (props.trackMouse && !isTouch) {
        document.addEventListener(mouseMove, onMove);
        document.addEventListener(mouseUp, onUp);
      }
      const { clientX, clientY } = isTouch ? event.touches[0] : event;
      const xy = rotateXYByAngle([clientX, clientY], props.rotationAngle);

      props.onTouchStartOrOnMouseDown &&
        props.onTouchStartOrOnMouseDown({ event });

      return {
        ...state,
        ...initialState,
        initial: xy.slice() as Vector2,
        xy,
        start: event.timeStamp || 0,
      };
    });
  };

  /**
   * Registers:
   * Calls:
   *  - `props.onSwipeStart`
   *  - `props.onSwiping`
   */
  const onMove = (event: HandledEvents) => {
    console.log('[vue3-swipable] getHandlers @ onMove');
    set((state, props) => {
      console.log('[vue3-swipable] getHandlers @ onMove @ SET');
      const isTouch = 'touches' in event;
      // Discount a swipe if additional touches are present after
      // a swipe has started.
      if (isTouch && event.touches.length > 1) {
        return state;
      }

      // if swipe has exceeded duration stop tracking
      if (event.timeStamp - state.start > props.swipeDuration) {
        return state.swiping ? {
 ...state,
swiping: false,
} : state;
      }

      const { clientX, clientY } = isTouch ? event.touches[0] : event;
      const [x, y] = rotateXYByAngle([clientX, clientY], props.rotationAngle);
      const deltaX = x - state.xy[0];
      const deltaY = y - state.xy[1];
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      const time = (event.timeStamp || 0) - state.start;
      const velocity = Math.sqrt(absX * absX + absY * absY) / (time || 1);
      const vxvy: Vector2 = [deltaX / (time || 1), deltaY / (time || 1)];

      const dir = getDirection(absX, absY, deltaX, deltaY);

      // if swipe is under delta and we have not started to track a swipe: skip update
      const delta =
        typeof props.delta === 'number'
          ? props.delta
          : props.delta[dir.toLowerCase() as Lowercase<SwipeDirections>] ||
            defaultProps.delta;
      if (absX < delta && absY < delta && !state.swiping) return state;

      const eventData = {
        absX,
        absY,
        deltaX,
        deltaY,
        dir,
        event,
        first: state.first,
        initial: state.initial,
        velocity,
        vxvy,
      };

      // call onSwipeStart if present and is first swipe event
      eventData.first && props.onSwipeStart && props.onSwipeStart(eventData);

      // call onSwiping if present
      props.onSwiping && props.onSwiping(eventData);

      // track if a swipe is cancelable (handler for swiping or swiped(dir) exists)
      // so we can call preventDefault if needed
      let cancelablePageSwipe = false;
      if (
        props.onSwiping ||
        props.onSwiped ||
        props[`onSwiped${dir}` as keyof SwipeableDirectionCallbacks]
      ) {
        cancelablePageSwipe = true;
      }

      if (
        cancelablePageSwipe &&
        props.preventScrollOnSwipe &&
        props.trackTouch &&
        event.cancelable
      ) {
        event.preventDefault();
      }

      return {
        ...state,
        // first is now always false
        first: false,
        eventData,
        swiping: true,
      };
    });
  };

  /**
   * Registers:
   * Calls:
   *  - `props.onSwiped`
   *  - `props.onTap`
   *  - `props.onTouchEndOrOnMouseUp`
   */
  const onEnd = (event: HandledEvents) => {
    console.log('[vue3-swipable] getHandlers @ onEnd');
    set((state, props) => {
      console.log('[vue3-swipable] getHandlers @ onEnd @ SET');
      let eventData: SwipeEventData | undefined;
      if (state.swiping && state.eventData) {
        // if swipe is less than duration fire swiped callbacks
        if (event.timeStamp - state.start < props.swipeDuration) {
          eventData = {
 ...state.eventData,
event,
};
          props.onSwiped && props.onSwiped(eventData);

          const onSwipedDir =
            props[
              `onSwiped${eventData.dir}` as keyof SwipeableDirectionCallbacks
            ];
          onSwipedDir && onSwipedDir(eventData);
        }
      } else {
        props.onTap && props.onTap({ event });
      }

      props.onTouchEndOrOnMouseUp && props.onTouchEndOrOnMouseUp({ event });

      return {
 ...state,
...initialState,
eventData,
};
    });
  };

  const cleanUpMouse = () => {
    console.log('[vue3-swipable] getHandlers @ cleanUpMouse');
    // safe to just call removeEventListener
    document.removeEventListener(mouseMove, onMove);
    document.removeEventListener(mouseUp, onUp);
  };

  const onUp = (e: HandledEvents) => {
    console.log('[vue3-swipable] getHandlers @ onUp');
    cleanUpMouse();
    onEnd(e);
  };

  /**
   * The value of passive on touchMove depends on `preventScrollOnSwipe`:
   * - true => { passive: false }
   * - false => { passive: true } // Default
   *
   * NOTE: When preventScrollOnSwipe is true, we attempt to call preventDefault to prevent scroll.
   *
   * props.touchEventOptions can also be set for all touch event listeners,
   * but for `touchmove` specifically when `preventScrollOnSwipe` it will
   * supersede and force passive to false.
   *
   */
  const attachTouch: AttachTouch = (el, props) => {
    console.log('[vue3-swipable] getHandlers @ attachTouch');
    let cleanup = () => {};
    if (el && el.addEventListener) {
      const baseOptions = {
        ...defaultProps.touchEventOptions,
        ...props.touchEventOptions,
      };
      // attach touch event listeners and handlers
      const tls: [
        typeof touchStart | typeof touchMove | typeof touchEnd,
        (e: HandledEvents) => void,
        { passive: boolean },
      ][] = [
        [touchStart, onStart, baseOptions],
        // preventScrollOnSwipe option supersedes touchEventOptions.passive
        [
          touchMove,
          onMove,
          {
            ...baseOptions,
            ...(props.preventScrollOnSwipe ? { passive: false } : {}),
          },
        ],
        [touchEnd, onEnd, baseOptions],
      ];
      tls.forEach(([e, h, o]) => el.addEventListener(e, h, o));
      // return properly scoped cleanup method for removing listeners, options not required
      cleanup = () => tls.forEach(([e, h]) => el.removeEventListener(e, h));
    }
    return cleanup;
  };

  const onRef = (el: HTMLElement | null) => {
    console.log('[vue3-swipable] getHandlers @ onRef');
    // "inline" ref functions are called twice on render, once with null then again with DOM element
    // ignore null here
    if (el === null) return;
    set((state, props) => {
      console.log('[vue3-swipable] getHandlers @ onRef @ SET');
      // if the same DOM el as previous just return state
      if (state.el === el) return state;

      const addState: { cleanUpTouch?: () => void } = {};
      // if new DOM el clean up old DOM and reset cleanUpTouch
      if (state.el && state.el !== el && state.cleanUpTouch) {
        state.cleanUpTouch();
        addState.cleanUpTouch = void 0;
      }
      // only attach if we want to track touch
      if (props.trackTouch && el) {
        addState.cleanUpTouch = attachTouch(el, props);
      }

      // store event attached DOM el for comparison, clean up, and re-attachment
      return {
 ...state,
el,
...addState,
};
    });
  };

  // set ref callback to attach touch event listeners
  const output: { ref: typeof onRef; onMouseDown?: typeof onStart } = {
    ref: onRef,
  };

  // if track mouse attach mouse down listener
  if (handlerProps.trackMouse) {
    output.onMouseDown = onStart;
  }

  console.log('[vue3-swipable] getHandlers @ return');

  return [output, attachTouch];
}

function updateTransientState(
  state: SwipeableState,
  props: SwipeablePropsWithDefaultOptions,
  previousProps: SwipeablePropsWithDefaultOptions,
  attachTouch: AttachTouch,
) {
  // if trackTouch is off or there is no el, then remove handlers if necessary and exit
  if (!props.trackTouch || !state.el) {
    if (state.cleanUpTouch) {
      state.cleanUpTouch();
    }

    return {
      ...state,
      cleanUpTouch: undefined,
    };
  }

  // trackTouch is on, so if there are no handlers attached, attach them and exit
  if (!state.cleanUpTouch) {
    return {
      ...state,
      cleanUpTouch: attachTouch(state.el, props),
    };
  }

  // trackTouch is on and handlers are already attached, so if preventScrollOnSwipe changes value,
  // remove and reattach handlers (this is required to update the passive option when attaching
  // the handlers)
  if (
    props.preventScrollOnSwipe !== previousProps.preventScrollOnSwipe ||
    props.touchEventOptions.passive !== previousProps.touchEventOptions.passive
  ) {
    state.cleanUpTouch();

    return {
      ...state,
      cleanUpTouch: attachTouch(state.el, props),
    };
  }

  return state;
}

function defaultCallbacks(node: Ref<HTMLElement | null>): SwipeableCallbacks {
  const defaultCallbacks = {
    onSwipedUp: (eventData: SwipeEventData) => {
      console.log('[vue3-swipable] defaultCallbacks @ onSwipedUp (swipe)');
      if (node.value !== null) {
        node.value.dispatchEvent(
          new CustomEvent(EVENT_SWIPED_UP, { detail: eventData }),
        );
      }
    },
    onSwipedDown: (eventData: SwipeEventData) => {
      console.log('[vue3-swipable] defaultCallbacks @ onSwipedDown (swipe)');
      if (node.value !== null) {
        node.value.dispatchEvent(
          new CustomEvent(EVENT_SWIPED_DOWN, { detail: eventData }),
        );
      }
    },
    onSwipedLeft: (eventData: SwipeEventData) => {
      console.log('[vue3-swipable] defaultCallbacks @ onSwipedLeft (swipe)');
      if (node.value !== null) {
        node.value.dispatchEvent(
          new CustomEvent(EVENT_SWIPED_LEFT, { detail: eventData }),
        );
      }
    },
    onSwipedRight: (eventData: SwipeEventData) => {
      console.log('[vue3-swipable] defaultCallbacks @ onSwipedRight (swipe)');
      if (node.value !== null) {
        node.value.dispatchEvent(
          new CustomEvent(EVENT_SWIPED_RIGHT, { detail: eventData }),
        );
      }
    },
    onSwipeStart: (eventData: SwipeEventData) => {
      console.log('[vue3-swipable] defaultCallbacks @ onSwipeStart (swipe)');
      if (node.value !== null) {
        node.value.dispatchEvent(
          new CustomEvent(EVENT_SWIPED_START, { detail: eventData }),
        );
      }
    },
    onSwiped: (eventData: SwipeEventData) => {
      console.log('[vue3-swipable] defaultCallbacks @ onSwiped (swipe)');
      if (node.value !== null) {
        node.value.dispatchEvent(
          new CustomEvent(EVENT_SWIPED, { detail: eventData }),
        );
      }
    },
    onSwiping: (eventData: SwipeEventData) => {
      console.log('[vue3-swipable] defaultCallbacks @ onSwiping (swipe)');
      if (node.value !== null) {
        node.value.dispatchEvent(
          new CustomEvent(EVENT_SWIPING, { detail: eventData }),
        );
      }
    },
    onTap: ({ event }: { event: HandledEvents }) => {
      console.log('[vue3-swipable] defaultCallbacks @ onTap (handled)');
      if (node.value !== null) {
        node.value.dispatchEvent(new CustomEvent(EVENT_TAP, { detail: event }));
      }
    },
    onTouchStartOrOnMouseDown: ({ event }: { event: HandledEvents }) => {
      console.log('[vue3-swipable] defaultCallbacks @ onTouchStartOrOnMouseDown (handled)');
      if (node.value !== null) {
        node.value.dispatchEvent(
          new CustomEvent(EVENT_TOUCHSTART_OR_MOUSEDOWN, { detail: event }),
        );
      }
    },
    onTouchEndOrOnMouseUp: ({ event }: { event: HandledEvents }) => {
      console.log('[vue3-swipable] defaultCallbacks @ onTouchEndOrOnMouseUp (handled)');
      if (node.value !== null) {
        node.value.dispatchEvent(
          new CustomEvent(EVENT_TOUCHEND_OR_MOUSEUP, { detail: event }),
        );
      }
    },
  };

  return defaultCallbacks;
}

export function useSwipeable(
  node: MaybeRefOrGetter<HTMLElement | undefined>,
  options?: MaybeRefOrGetter<SwipeableProps>,
)/*: {
  Ref<() => void>;
} */{
  const dOptions = computed<SwipeableProps | null>(
    () => toValue(options) ?? null,
  );
  const trackMouse = computed(() =>
    dOptions.value !== undefined && dOptions.value !== null
      ? dOptions.value.trackMouse
      : false,
  );

  const handlerSetter = computed<HandlerSetter>(() => {
    let transientState = { ...initialState };
    let transientProps = { ...defaultProps };

    const previousProps = { ...transientProps };
    // default handlers that emit new events
    const nodePlaceholder = ref<null | HTMLElement>(null);
    const defaultHandlers = defaultCallbacks(nodePlaceholder);

    transientProps = {
      ...defaultProps,
      ...dOptions.value,
      ...defaultHandlers,
    };

    // Force defaults for config properties
    let defaultKey: keyof ConfigurationOptions;
    for (defaultKey in defaultProps) {
      if (transientProps[defaultKey] === void 0) {
        (transientProps[defaultKey] as any) = defaultProps[defaultKey];
      }
    }

    const [handlers, attachTouch] = getHandlers(
      (stateSetter) =>
        (transientState = stateSetter(transientState, transientProps)),
      { trackMouse: trackMouse.value },
    );

    transientState = updateTransientState(
      transientState,
      transientProps,
      previousProps,
      attachTouch,
    );

    const returna: HandlerSetter = {
      attachTouch,
      handlerRef: handlers,
      transientProps,
      nodePlaceholder,
    };
    return returna;
  });
  const previousCancel = ref<() => void>(() => {});

  watch(
    [handlerSetter, () => toValue(node)],
    ([newHandlerSetter, newNode], [oldHandlerSetter, oldNode]) => {
        if (newNode === undefined || newNode === null) {
          return;
        }
        const { attachTouch, handlerRef, transientProps, nodePlaceholder } =
          newHandlerSetter;
        console.log('[vue3-swipable] REGISTERING NODE TO HANDLER', [
{
 newHandlerSetter,
newNode,
},
{
 oldHandlerSetter,
oldNode,
},
]);
        previousCancel.value();
        previousCancel.value = attachTouch(toValue(newNode), transientProps);
        nodePlaceholder.value = newNode;
        handlerRef.ref(toValue(newNode));
      },
    {
      immediate: true,
    },
  );
}
